import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { format, addDays, subDays, startOfWeek, isSameDay } from "date-fns";
import Layout from "../../Components/Layout";
import "../../Assets/CSS/AdminTimeSheet.css";
import { toast } from "react-toastify";
import axios from "axios";
import {
  BigText,
  DarkBold,
  DarkOne,
  LightDark,
  LighterBig,
  LighterBold,
  Lightly,
  middleOne,
  smallText,
} from "../../Assets/CSS/Text";
import Model from "../../Components/Model/Model";
import ViewModel from "../../Components/Model/ViewModel";
import { Pointer } from "lucide-react";
import Img from "../../Assets/Images/fingure-print.png";
import { useLocation } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const AdminTimeSheet = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [weeklyData, setWeeklyData] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [modalLocation, setModalLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); // State for search input

  const [viewMode, setViewMode] = useState("daily");
  const todayDate = format(new Date(), "yyyy-MM-dd");
  const [selectedDate, setSelectedDate] = useState(todayDate);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentWeekStartDate, setCurrentWeekStartDate] = useState(
    startOfWeek(new Date(), { weekStartsOn: 1 }),
  );
  const [viewTaskModal, setViewTaskModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(0); // Page index starts at 0
  const [pageSize] = useState(10); // 10 records per page
  const [showAddAttendanceModal, setShowAddAttendanceModal] = useState(false);
  const [attendanceForm, setAttendanceForm] = useState({
    empId: "",
    timesheetDate: format(new Date(), "yyyy-MM-dd"),
    checkInTime: "",
    checkOutTime: "",
    userLocation: "",
  });
  const [locationLoading, setLocationLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const employeeOptions = Array.from(
    new Set(
      weeklyData
        .flatMap((dayData) => dayData.employees || [])
        .map((emp) => emp.empId || emp.employeeId || emp.employeeCode)
        .filter(Boolean),
    ),
  );

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by this browser."));
        return;
      }

      setLocationLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const location = `Lat: ${latitude}, Long: ${longitude}`;
          setAttendanceForm((prev) => ({
            ...prev,
            userLocation: location,
          }));
          setLocationLoading(false);
          resolve(location);
        },
        () => {
          const message = "Unable to fetch current location.";
          toast.error(message);
          setLocationLoading(false);
          reject(new Error(message));
        },
        { enableHighAccuracy: true, timeout: 20000 },
      );
    });
  };

  const toDateTimeLocalValue = (date) => {
    const pad = (value) => String(value).padStart(2, "0");

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const buildAttendanceFormForSelectedDate = (dateValue = selectedDate) => {
    const chosenDate = dateValue || format(new Date(), "yyyy-MM-dd");
    const now = new Date();
    const checkInDate = new Date(
      `${chosenDate}T${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
    );
    const checkOutDate = new Date(checkInDate.getTime() + 60 * 1000);

    return {
      empId: employeeOptions[0] || "",
      timesheetDate: chosenDate,
      checkInTime: toDateTimeLocalValue(checkInDate),
      checkOutTime: toDateTimeLocalValue(checkOutDate),
      userLocation: "",
    };
  };

  const resetAttendanceForm = () => {
    setAttendanceForm(buildAttendanceFormForSelectedDate(selectedDate));
  };

  const openAddAttendanceModal = () => {
    setAttendanceForm(buildAttendanceFormForSelectedDate(selectedDate));
    setShowAddAttendanceModal(true);
  };

  const handleAddAttendanceSubmit = async (event) => {
    event.preventDefault();

    const { empId, timesheetDate, checkInTime, checkOutTime } = attendanceForm;

    if (!empId || !timesheetDate || !checkInTime || !checkOutTime) {
      toast.error("Please fill in all attendance fields before submitting.");
      return;
    }

    const checkInDate = new Date(checkInTime);
    let checkOutDate = new Date(checkOutTime);

    if (
      Number.isNaN(checkInDate.getTime()) ||
      Number.isNaN(checkOutDate.getTime())
    ) {
      toast.error("Please enter valid check-in and check-out times.");
      return;
    }

    if (checkOutDate <= checkInDate) {
      checkOutDate = new Date(checkInDate.getTime() + 60 * 1000);
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Authentication token not found. Please login again.");
      return;
    }

    let finalLocation = attendanceForm.userLocation;

    try {
      if (!finalLocation) {
        finalLocation = await getCurrentLocation();
      }
    } catch (error) {
      toast.error(error.message || "Unable to fetch current location.");
      return;
    }

    const payload = {
      empId,
      timesheetDate,
      userLocation: finalLocation,
      checkInTime: checkInDate.toISOString(),
      checkOutTime: checkOutDate.toISOString(),
    };

    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      await axios.post(
        `${baseUrl}/api/v1/timesheets/create-timesheet`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      toast.success("Attendance added successfully");
      setShowAddAttendanceModal(false);
      resetAttendanceForm();
      const start = format(currentWeekStartDate, "yyyy-MM-dd");
      const end = format(addDays(currentWeekStartDate, 6), "yyyy-MM-dd");
      fetchWeeklyData(start, end);
    } catch (error) {
      console.error("Add attendance error:", error);

      const responseMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to add attendance.";

      const message =
        typeof responseMessage === "string"
          ? responseMessage
          : JSON.stringify(responseMessage);

      toast.error(message);
    }
  };

  // Function to open ViewModel
  const handleViewTask = (emp) => {
    setSelectedEmployee(emp);
    setViewTaskModal(true);
  };
  const toggleDropdown = () => setShowDropdown((prev) => !prev);
  const dayData = { hours: 8, status: "Present" }; // or whatever structure you expect

  function formatTime(timeStr) {
    if (!timeStr || typeof timeStr !== "string") return "-";

    try {
      // Handle different time formats
      let timePart;

      // Check if it's a full datetime string (e.g., "25-09-2025 11:16::46")
      if (
        timeStr.includes(" ") &&
        (timeStr.includes("::") || timeStr.includes(":"))
      ) {
        // Split by space to get date and time parts
        const [datePart, timePartStr] = timeStr.split(" ");

        // Handle double colon format (::) and single colon format (:)
        timePart = timePartStr.replace("::", ":");

        // Validate the date part (DD-MM-YYYY format)
        const [day, month, year] = datePart.split("-");
        const dayNum = parseInt(day);
        const monthNum = parseInt(month);
        const yearNum = parseInt(year);

        // Check for invalid date (e.g., 24th month, 32nd day, etc.)
        if (
          monthNum > 12 ||
          monthNum < 1 ||
          dayNum > 31 ||
          dayNum < 1 ||
          yearNum < 1900
        ) {
          console.warn(`Invalid date format: ${datePart}`);
          return "Invalid Date";
        }
      } else {
        // Handle time-only format (e.g., "15:07:16")
        timePart = timeStr.replace("::", ":");
      }

      // Parse the time part
      const [hours, minutes, seconds] = timePart.split(":");

      // Validate time components
      const hoursNum = parseInt(hours);
      const minutesNum = parseInt(minutes);
      const secondsNum = parseInt(seconds || "0");

      if (
        hoursNum > 23 ||
        hoursNum < 0 ||
        minutesNum > 59 ||
        minutesNum < 0 ||
        secondsNum > 59 ||
        secondsNum < 0
      ) {
        console.warn(`Invalid time format: ${timePart}`);
        return "Invalid Time";
      }

      // Create a valid date string for today with the parsed time
      const today = new Date();
      const dateStr = today.toISOString().split("T")[0]; // Use current date
      const fullTimeStr = `${dateStr}T${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}:${(seconds || "00").padStart(2, "0")}`;

      // Parse with server's timezone (assuming server time is IST)
      const serverTime = new Date(fullTimeStr);

      // Check if the date is valid
      if (isNaN(serverTime.getTime())) {
        console.warn(`Invalid date/time: ${fullTimeStr}`);
        return "Invalid Date";
      }

      const options = {
        timeZone: "Asia/Kolkata", // Explicitly set to Kolkata (IST)
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      };

      // Convert to Kolkata timezone
      const kolkataTime = serverTime.toLocaleString("en-US", options);
      return kolkataTime;
    } catch (error) {
      console.error(`Error formatting time: ${timeStr}`, error);
      return "Invalid Format";
    }
  }
  const monthNameToNumber = {
    January: 1,
    February: 2,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12,
  };

  console.log(selectedDate);
  const fetchWeeklyData = async (startDate, endDate) => {
    try {
      let url;

      //  url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/timesheets/employees/date?date=${selectedDate}`;
      //  url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/timesheets/employees/date-range?startDate=${startDate}&endDate=${endDate}`;

      if (viewMode === "daily") {
        url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/timesheets/employees/date?date=${selectedDate}&page=${0}&size=${500}`;
      } else {
        url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/timesheets/employees/date-range?startDate=${startDate}&endDate=${endDate}&page=${0}&size=${500}`;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const data = await response.json();
      const records = data?.content || [];

      const groupData = groupDataByDate(records);

      console.log("Raw API response:", data);
      console.log("Extracted records:", records);

      const filtered =
        viewMode === "daily"
          ? groupData.filter((item) => item.date === selectedDate)
          : groupData.filter(
              (item) => item.date >= startDate && item.date <= endDate,
            );

      setWeeklyData(filtered);
      console.log("TimeSheeeee data", filtered);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setWeeklyData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const start = format(currentWeekStartDate, "yyyy-MM-dd");
    const end = format(addDays(currentWeekStartDate, 6), "yyyy-MM-dd");
    setStartDate(start);
    setEndDate(end);

    fetchWeeklyData(start, end);
  }, [currentWeekStartDate, viewMode, selectedDate]);
  // Reset pagination when switching between daily and weekly view
  useEffect(() => {
    setCurrentPage(0);
  }, [viewMode, searchQuery, selectedDate, startDate, endDate]);
  function groupDataByDate(dataArray) {
    const groupedData = dataArray.reduce((acc, item) => {
      // Use timesheetDate instead of date
      const dateKey = item.timesheetDate || item.date;
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(item);
      return acc;
    }, {});

    return Object.keys(groupedData).map((date) => ({
      date,
      employees: groupedData[date],
    }));
  }

  const generateWeekDates = () => {
    return Array.from({ length: 7 }, (_, i) =>
      addDays(currentWeekStartDate, i),
    );
  };

  const handlePrevWeek = () => {
    setCurrentWeekStartDate((prev) => subDays(prev, 7));
    setCurrentPage(0); // Reset pagination
  };

  const handleNextWeek = () => {
    setCurrentWeekStartDate((prev) => addDays(prev, 7));
    setCurrentPage(0); // Reset pagination
  };
  const showMapModal = (lat, long) => {
    // setModalLocation({ lat, long });
    console.log(lat, long, "map data");
    const url = `https://www.google.com/maps?q=${lat},${long}`;
    window.open(url, "_blank");
  };

  const closeModal = () => {
    setModalLocation(null);
  };

  const weekDates = generateWeekDates();
  const today = new Date();
  const handleShowDownloadModal = () => setShowDownloadModal(true);

  const handleCloseModal = () => {
    setShowModal(false);
    setShowDownloadModal(false);
  };

  const handleDownloadTimeSheet = async ({ year, month }) => {
    if (!year || !month) {
      toast.error("Please select both Year and Month");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized: Token not found");
      return;
    }
    const monthNumber = isNaN(month) ? monthNameToNumber[month] : month;
    try {
      const response = await axios.get(
        // `${process.env.REACT_APP_BACKEND_URL}/api/v1/attendance/export/monthly-timesheet?year=${year}&month=${month}`,
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/timesheets/export/monthly-timesheet?year=${year}&month=${monthNumber}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "*/*",
          },
          responseType: "blob",
        },
      );

      if (response.status === 200) {
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", `TimeSheet_${year}_${month}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("File downloaded successfully");
        handleCloseModal();
      }
    } catch (error) {
      console.error("Download Error:", error);
      let rawMessage = error?.response?.data?.message;

      if (typeof rawMessage !== "string") {
        rawMessage = "Something went wrong!";
      }

      toast.error(
        rawMessage.length > 100 ? rawMessage.slice(0, 100) + "..." : rawMessage,
      );
    }
  };
  const handleDownloadAttendance = async ({ year, month }) => {
    if (!year || !month) {
      toast.error("Please select both Year and Month");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized: Token not found");
      return;
    }
    const monthNumber = isNaN(month) ? monthNameToNumber[month] : month;
    try {
      const response = await axios.get(
        // `${process.env.REACT_APP_BACKEND_URL}/api/v1/attendance/export/monthly-attendance?year=${year}&month=${month}`,
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/timesheets/export/monthly-attendance?year=${year}&month=${monthNumber}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "*/*",
          },
          responseType: "blob",
        },
      );

      if (response.status === 200) {
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", `Attendance_${year}_${month}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("File downloaded successfully");
        handleCloseModal();
      }
    } catch (error) {
      console.error("Download Error:", error);
      let rawMessage = error?.response?.data?.message;

      if (typeof rawMessage !== "string") {
        rawMessage = "Something went wrong!";
      }

      toast.error(
        rawMessage.length > 100 ? rawMessage.slice(0, 100) + "..." : rawMessage,
      );
    }
  };
  const capitalizeWords = (str) =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());

  const allEmployees = weeklyData.flatMap((dayData, dayIndex) =>
    dayData.employees.map((emp, empIndex) => ({
      ...emp,
      dayIndex,
      empIndex,
    })),
  );
  const selectedDateFormatted = format(new Date(selectedDate), "yyyy-MM-dd");

  const punchInCount =
    weeklyData
      .find((d) => d.date === selectedDate)
      ?.employees?.filter((emp) => emp.checkInTime)?.length || 0;

  const isSmallScreen = useMediaQuery("(max-width:768px)");
  const headingStyle = isSmallScreen ? DarkOne : BigText;
  const filteredEmployees = allEmployees.filter((emp) =>
    (emp.empName || emp.employeeName || "")
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredEmployees.length / pageSize);

  const paginatedEmployees = filteredEmployees.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize,
  );
  return (
    <Layout>
      <div className="backgound-main-content">
        <div className="timesheet-headesr">
          <div className="header-title">
            <div className="d-flex align-items-center gap-2">
              <h2 style={headingStyle}>Employee’s Timesheet</h2>

              <span className="total-punch">
                <img src={Img} alt="Fingerprint" className="fingure-img" />
              </span>
              <span style={LighterBig}>{punchInCount}</span>
            </div>

            <div className="d-flex flex-column align-items-end position-relative download-container">
              {isSmallScreen ? (
                <>
                  <div className="timesheet-action-stack">
                    <button
                      className="attendance-add-btn"
                      onClick={openAddAttendanceModal}
                    >
                      <Icon icon="mdi:calendar-check" width="18" height="18" />
                      Add Attendance
                    </button>
                    <button
                      className="downloadSheet-btn"
                      onClick={toggleDropdown}
                    >
                      <Icon
                        icon="material-symbols:download-rounded"
                        width="20"
                        height="20"
                      />
                    </button>
                  </div>

                  {showDropdown && (
                    <div className="dropdown-box">
                      <button
                        className="download-btn"
                        onClick={() => {
                          setShowDropdown(false);
                          setShowModal(true);
                        }}
                      >
                        <Icon
                          icon="material-symbols:download-rounded"
                          width="20"
                          height="20"
                        />
                        Attendance Report
                      </button>
                      <button
                        className="download-btn"
                        onClick={() => {
                          setShowDropdown(false);
                          handleShowDownloadModal();
                        }}
                      >
                        <Icon
                          icon="material-symbols:download-rounded"
                          width="20"
                          height="20"
                        />
                        Download Timesheet
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="d-flex gap-3 flex-wrap justify-content-end timesheet-action-row">
                  <button
                    className="attendance-add-btn"
                    onClick={openAddAttendanceModal}
                  >
                    <Icon icon="mdi:calendar-check" width="18" height="18" />
                    Add Attendance
                  </button>
                  <button
                    className="download-btn"
                    onClick={() => setShowModal(true)}
                  >
                    <Icon
                      icon="material-symbols:download-rounded"
                      width="20"
                      height="20"
                    />
                    Attendance Report
                  </button>
                  <button
                    className="download-btn"
                    onClick={handleShowDownloadModal}
                  >
                    <Icon
                      icon="material-symbols:download-rounded"
                      width="20"
                      height="20"
                    />
                    Monthly Report
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="timesheet-controls">
            <div className="search-bar">
              <input
                type="text"
                className="border-none"
                placeholder="Search employee name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Icon
                icon="iconamoon:search"
                width="20"
                height="20"
                color="rgba(0, 175, 239, 1)"
              />
            </div>

            <div className="calender">
              <div className="toggle-buttons">
                <button
                  className={viewMode === "daily" ? "active" : "inactive"}
                  onClick={() => setViewMode("daily")}
                >
                  Daily
                </button>
                <button
                  className={viewMode === "weekly" ? "active" : "inactive"}
                  onClick={() => setViewMode("weekly")}
                >
                  Weekly
                </button>
              </div>

              {viewMode === "weekly" ? (
                <div className="">
                  <div className="date-picker">
                    <Icon
                      icon="uil:calender"
                      width="20"
                      height="20"
                      color="#00AFEF"
                    />
                    <span className="dates">
                      {format(weekDates[0], "MMM d")} -{" "}
                      {format(weekDates[6], "MMM d ''yy")}
                    </span>
                    <div className="ms-2">
                      <button className="na-btn" onClick={handlePrevWeek}>
                        <Icon
                          icon="iconamoon:arrow-left-2"
                          width="20"
                          height="20"
                          color="#606060"
                        />
                      </button>
                      <button className="na-btn" onClick={handleNextWeek}>
                        <Icon
                          icon="iconamoon:arrow-right-2"
                          width="20"
                          height="20"
                          color="#606060"
                        />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="daily-date-view">
                  <input
                    type="date"
                    className="calendar-input"
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="timesheet-table">
            {loading ? (
              isMobile ? (
                // Mobile skeleton cards
                Array.from({ length: 5 }).map((_, index) => (
                  <div className="timesheet-card" key={index}>
                    <div className="card-headers mb-2">
                      <Skeleton width={50} height={20} />
                    </div>
                    <div className="card-body">
                      <div className="emp-info">
                        <Skeleton circle width={36} height={36} />
                        <h3 style={middleOne}>
                          <Skeleton width={120} />
                        </h3>
                      </div>
                      <div className="card-row">
                        <Skeleton width={80} />
                      </div>
                      <div className="card-row">
                        <Skeleton width={80} />
                      </div>
                      <div className="card-row">
                        <Skeleton width={150} />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                // Desktop skeleton table rows
                <table>
                  <thead>
                    <tr>
                      <th style={LighterBold}>ID</th>
                      <th style={LighterBold}>Employee Name</th>
                      <th style={LighterBold}>Login Time</th>
                      <th style={LighterBold}>Login Location</th>
                      <th style={LighterBold}>Logout Time</th>
                      <th style={LighterBold}>Logout Location</th>
                      <th style={LighterBold}>Tasks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <tr key={index}>
                        <td>
                          <Skeleton width={50} />
                        </td>
                        <td>
                          <Skeleton width={150} />
                        </td>
                        <td>
                          <Skeleton width={100} />
                        </td>
                        <td>
                          <Skeleton width={120} />
                        </td>
                        <td>
                          <Skeleton width={100} />
                        </td>
                        <td>
                          <Skeleton width={120} />
                        </td>
                        <td>
                          <Skeleton width={80} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : filteredEmployees.length === 0 ? (
              // When there is no data
              <p
                style={{ textAlign: "center", padding: "20px", color: "#999" }}
              >
                No attendance records available for the selected date(s).
              </p>
            ) : isMobile ? (
              // weeklyData
              //   .flatMap((dayData, dayIndex) =>
              //     dayData.employees.map((emp, empIndex) => ({
              //       ...emp,
              //       dayIndex,
              //       empIndex,
              //     })),
              //   )
              //   .filter((emp) =>
              //     (emp.empName || emp.employeeName || "")
              //       .toLowerCase()
              //       .includes(searchQuery.toLowerCase()),
              //   ).length === 0 ?
              filteredEmployees.length === 0 ? (
                <p
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#999",
                  }}
                >
                  No results found
                </p>
              ) : (
                // weeklyData
                //   .flatMap((dayData, dayIndex) =>
                //     dayData.employees.map((emp, empIndex) => ({
                //       ...emp,
                //       dayIndex,
                //       empIndex,
                //     })),
                //   )
                //   .filter((emp) =>
                //     (emp.empName || emp.employeeName || "")
                //       .toLowerCase()
                //       .includes(searchQuery.toLowerCase()),
                //   )
                paginatedEmployees.map((emp) => (
                  <div
                    className="timesheet-card"
                    key={`${emp.dayIndex}-${emp.empIndex}`}
                  >
                    <div className="card-headers mb-2" style={Lightly}>
                      {emp.empId}
                    </div>

                    <div className="card-body">
                      <div className="emp-info">
                        <div
                          className="rounded-circle d-flex justify-content-center align-items-center"
                          style={{
                            width: "36px",
                            height: "36px",
                            fontSize: "12px",
                            color: "#999",
                            backgroundColor: "#F0F0F0",
                            border: "1px solid #F0F0F0",
                          }}
                        >
                          <Icon icon="proicons:person" className="fs-5" />
                        </div>

                        <div>
                          <h3 style={middleOne}>
                            {capitalizeWords(
                              emp.empName || emp.employeeName || "N/A",
                            )}
                          </h3>
                        </div>
                      </div>

                      <div className="card-row">
                        <span
                          style={{
                            ...smallText,
                            textAlign: "left",
                          }}
                          className="timeSheetlabel"
                        >
                          Login:
                        </span>

                        <span style={LightDark}>
                          {formatTime(emp.checkInTime)}
                        </span>
                      </div>

                      <div className="card-row">
                        <span
                          style={{
                            ...smallText,
                            textAlign: "left",
                          }}
                          className="timeSheetlabel"
                        >
                          Logout:
                        </span>

                        <span style={LightDark}>
                          {formatTime(emp.checkOutTime)}
                        </span>
                      </div>

                      <div className="card-row">
                        <span
                          style={{
                            ...smallText,
                            textAlign: "left",
                          }}
                          className="timeSheetlabel"
                        >
                          Task:
                        </span>

                        <span style={LightDark}>
                          {emp.taskDescription || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )
            ) : (
              // Desktop view: table
              <table>
                <thead>
                  <tr>
                    <th style={LighterBold}>ID</th>
                    <th style={LighterBold}>Employee Name</th>
                    <th style={LighterBold}>Login Time</th>
                    <th style={LighterBold}>Login Location</th>
                    <th style={LighterBold}>Logout Time</th>
                    <th style={LighterBold}>Logout Location</th>
                    <th style={LighterBold}>Tasks</th>
                  </tr>
                </thead>
                <tbody>
                  {weeklyData
                    .flatMap((dayData, dayIndex) =>
                      dayData.employees.map((emp, empIndex) => ({
                        ...emp,
                        dayIndex,
                        empIndex,
                      })),
                    )
                    .filter((emp) =>
                      (emp.empName || emp.employeeName || "")
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase()),
                    ).length === 0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: "#000000",
                          fontSize: "15px",
                        }}
                      >
                        No Results found
                      </td>
                    </tr>
                  ) : (
                    // weeklyData
                    //   .flatMap((dayData, dayIndex) =>
                    //     dayData.employees.map((emp, empIndex) => ({
                    //       ...emp,
                    //       dayIndex,
                    //       empIndex,
                    //     })),
                    //   )
                    //   .filter((emp) =>
                    //     (emp.empName || emp.employeeName || "")
                    //       .toLowerCase()
                    //       .includes(searchQuery.toLowerCase()),
                    //   )
                    paginatedEmployees.map((emp, index) => (
                      <tr
                        key={`${emp.dayIndex}-${emp.empIndex}`}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
                        }}
                      >
                        <td style={Lightly}>{emp.empId}</td>

                        <td style={DarkBold}>
                          {capitalizeWords(
                            emp.empName || emp.employeeName || "N/A",
                          )}
                        </td>

                        <td style={LightDark}>{formatTime(emp.checkInTime)}</td>

                        <td
                          style={{
                            ...LightDark,
                            color: "#00AFEF",
                            cursor: "pointer",
                          }}
                        >
                          {emp.checkInLocation ? (
                            <span
                              title="Open in Google Maps"
                              onClick={() => {
                                if (emp.checkInLocation.includes("Lat:")) {
                                  const [rawLat, rawLong] =
                                    emp.checkInLocation.split(", ");

                                  const lat = rawLat
                                    .replace("Lat: ", "")
                                    .trim();

                                  const long = rawLong
                                    .replace("Long: ", "")
                                    .trim();

                                  showMapModal(lat, long);
                                } else {
                                  console.log(
                                    "Location name:",
                                    emp.checkInLocation,
                                  );
                                }
                              }}
                            >
                              View Map
                            </span>
                          ) : (
                            "Not Available"
                          )}
                        </td>

                        <td style={LightDark}>
                          {formatTime(emp.checkOutTime)}
                        </td>

                        <td
                          style={{
                            ...LightDark,
                            color: "#00AFEF",
                            cursor: "pointer",
                          }}
                        >
                          {emp.checkOutLocation ? (
                            <span
                              title="Open in Google Maps"
                              onClick={() => {
                                if (emp.checkOutLocation.includes("Lat:")) {
                                  const [rawLat, rawLong] =
                                    emp.checkOutLocation.split(", ");

                                  const lat = rawLat
                                    .replace("Lat: ", "")
                                    .trim();

                                  const long = rawLong
                                    .replace("Long: ", "")
                                    .trim();

                                  showMapModal(lat, long);
                                } else {
                                  console.log(
                                    "Location name:",
                                    emp.checkOutLocation,
                                  );
                                }
                              }}
                            >
                              View Map
                            </span>
                          ) : (
                            "N/A"
                          )}
                        </td>

                        <td
                          style={{
                            ...LightDark,
                            color: "#00AFEF",
                            cursor: "pointer",
                          }}
                          onClick={() => handleViewTask(emp)}
                        >
                          View
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}

            {/* Pagination block – ADD HERE */}
            {totalPages > 1 && (
              <div
                className="pagination-controls"
                style={{ marginTop: "30px", textAlign: "center" }}
              >
                <button
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                >
                  <Icon
                    icon="iconamoon:arrow-left-2"
                    width="20"
                    height="20"
                    color="#606060"
                  />
                </button>
                <span style={{ margin: "0 10px" }}>
                  Page {currentPage + 1} of {totalPages}
                </span>
                <button
                  disabled={currentPage + 1 >= totalPages}
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                >
                  <Icon
                    icon="iconamoon:arrow-right-2"
                    width="20"
                    height="20"
                    color="#606060"
                  />
                </button>
              </div>
            )}

            {/* Showing records */}
            {weeklyData.length > 0 && (
              <p style={{ textAlign: "center", marginTop: "10px" }}>
                Showing {currentPage * pageSize + 1} to{" "}
                {Math.min(
                  (currentPage + 1) * pageSize,
                  filteredEmployees.length,
                )}
                of {filteredEmployees.length}
              </p>
            )}
          </div>
        </div>
      </div>

      {showAddAttendanceModal && (
        <div
          className="attendance-modal-backdrop"
          onClick={() => setShowAddAttendanceModal(false)}
        >
          <div
            className="attendance-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="attendance-modal-header">
              <div>
                <p className="attendance-modal-eyebrow">ATTENDANCE</p>
                <h3>Add Attendance</h3>
              </div>
              <button
                type="button"
                className="attendance-close-btn"
                onClick={() => setShowAddAttendanceModal(false)}
              >
                ×
              </button>
            </div>

            <form
              onSubmit={handleAddAttendanceSubmit}
              className="attendance-form"
            >
              <div className="attendance-form-grid">
                <label>
                  Employee ID
                  <select
                    value={attendanceForm.empId}
                    onChange={(event) =>
                      setAttendanceForm((prev) => ({
                        ...prev,
                        empId: event.target.value,
                      }))
                    }
                  >
                    <option value="">Select employee</option>
                    {employeeOptions.length > 0 ? (
                      employeeOptions.map((id) => (
                        <option key={id} value={id}>
                          {id}
                        </option>
                      ))
                    ) : (
                      <option value="RAK-123">RAK-123</option>
                    )}
                  </select>
                </label>

                <label>
                  Date
                  <input
                    type="date"
                    value={attendanceForm.timesheetDate}
                    onChange={(event) => {
                      const nextDate = event.target.value;
                      const now = new Date();
                      const checkInDate = new Date(
                        `${nextDate}T${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
                      );
                      const checkOutDate = new Date(
                        checkInDate.getTime() + 60 * 1000,
                      );

                      setAttendanceForm((prev) => ({
                        ...prev,
                        timesheetDate: nextDate,
                        checkInTime: toDateTimeLocalValue(checkInDate),
                        checkOutTime: toDateTimeLocalValue(checkOutDate),
                      }));
                    }}
                  />
                </label>

                <label>
                  Check-in Time
                  <input
                    type="datetime-local"
                    value={attendanceForm.checkInTime}
                    onChange={(event) =>
                      setAttendanceForm((prev) => ({
                        ...prev,
                        checkInTime: event.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Check-out Time
                  <input
                    type="datetime-local"
                    value={attendanceForm.checkOutTime}
                    onChange={(event) =>
                      setAttendanceForm((prev) => ({
                        ...prev,
                        checkOutTime: event.target.value,
                      }))
                    }
                  />
                </label>
              </div>

              <div className="attendance-form-actions">
                <button
                  type="button"
                  className="attendance-cancel-btn"
                  onClick={() => setShowAddAttendanceModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="attendance-submit-btn">
                  Submit Attendance
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ViewModel
        show={viewTaskModal}
        onClose={() => setViewTaskModal(false)}
        employeeData={selectedEmployee}
      />

      {/* Download TimeSheet Modal */}
      <Model
        show={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onSubmit={handleDownloadTimeSheet}
        title="Download TimeSheet"
      />
      {/* Download Attendance Sheet */}
      <Model
        show={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleDownloadAttendance}
        title="Download Monthly Attendance"
      />
      {/*  */}
    </Layout>
  );
};

export default AdminTimeSheet;
